/**
 * UpdateAppJobModal Component
 *
 * Modal for updating an existing appJob.
 */

import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Select } from '../../../components/ui/Select';
import { Input } from '../../../components/ui/Input';
import { Textarea } from '../../../components/ui/Textarea';
import { Alert } from '../../../components/ui/Alert';
import { Spinner } from '../../../components/ui/Spinner';
import { FormField } from '../../../components/forms/FormField';
import { useAppJobDetails } from '../hooks/useAppJobDetails';
import { useUpdateAppJob } from '../hooks/useUpdateAppJob';
import {
  updateAppJobFormSchema,
  parsePayload,
  formatPayload,
  commonTimezones,
  cronPresets,
  type UpdateAppJobFormData,
} from '../../../lib/validators/app-job';
import type { AppJobSanitized } from '../../../types/app-job.types';

interface UpdateAppJobModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  appJob: AppJobSanitized;
}

/**
 * UpdateAppJobModal component
 */
export const UpdateAppJobModal: React.FC<UpdateAppJobModalProps> = ({
  isOpen,
  onClose,
  tenantId,
  appJob,
}) => {
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    setValue,
  } = useForm<UpdateAppJobFormData>({
    resolver: zodResolver(updateAppJobFormSchema),
    defaultValues: {
      description: appJob.description || '',
      functionName: appJob.functionName,
      payloadInput: formatPayload(appJob.payload),
      cronExpression: appJob.cronExpression,
      timezone: appJob.timezone,
    },
  });

  // Reset form when appJob changes
  useEffect(() => {
    reset({
      description: appJob.description || '',
      functionName: appJob.functionName,
      payloadInput: formatPayload(appJob.payload),
      cronExpression: appJob.cronExpression,
      timezone: appJob.timezone,
    });
  }, [appJob, reset]);

  const { mutate: updateAppJob, isPending } = useUpdateAppJob(tenantId, {
    onSuccess: () => {
      setSuccessMessage('App job updated successfully!');
      setErrorMessage('');
      setTimeout(() => {
        onClose();
        setSuccessMessage('');
      }, 1500);
    },
    onError: (error) => {
      setErrorMessage(error);
      setSuccessMessage('');
    },
  });

  const onSubmit = (data: UpdateAppJobFormData) => {
    setErrorMessage('');
    setSuccessMessage('');

    // Parse payload if provided
    let payload: Record<string, unknown> | undefined;
    if (data.payloadInput?.trim()) {
      try {
        payload = parsePayload(data.payloadInput);
      } catch (error) {
        setErrorMessage(error instanceof Error ? error.message : 'Invalid JSON payload');
        return;
      }
    }

    // Only include changed fields
    const updates: Record<string, unknown> = {};
    if (data.description !== appJob.description) {
      updates.description = data.description || undefined;
    }
    if (data.functionName && data.functionName !== appJob.functionName) {
      updates.functionName = data.functionName;
    }
    if (data.cronExpression && data.cronExpression !== appJob.cronExpression) {
      updates.cronExpression = data.cronExpression;
    }
    if (data.timezone && data.timezone !== appJob.timezone) {
      updates.timezone = data.timezone;
    }
    if (
      data.payloadInput !== formatPayload(appJob.payload) &&
      payload !== undefined
    ) {
      updates.payload = payload;
    }

    if (Object.keys(updates).length === 0) {
      setErrorMessage('No changes detected');
      return;
    }

    updateAppJob({
      appJobId: appJob.appJobId,
      data: updates as any,
    });
  };

  const handleClose = () => {
    if (!isPending) {
      reset();
      setErrorMessage('');
      setSuccessMessage('');
      onClose();
    }
  };

  const handleCronPresetChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value) {
      setValue('cronExpression', value, { shouldDirty: true });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <Modal.Header>Edit App Job: {appJob.name}</Modal.Header>
      <Modal.Body>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Info */}
          <div className="bg-gray-50 p-3 rounded-lg">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">Application</span>
              <span className="font-medium text-gray-900">{appJob.applicationCode}</span>
            </div>
            <div className="flex items-center justify-between text-sm mt-2">
              <span className="text-gray-500">Service Account</span>
              <span className="font-medium text-gray-900">
                {appJob.serviceAccountName || 'N/A'}
              </span>
            </div>
          </div>

          {/* Error Message */}
          {errorMessage && <Alert variant="error">{errorMessage}</Alert>}

          {/* Success Message */}
          {successMessage && <Alert variant="success">{successMessage}</Alert>}

          {/* Function Name */}
          <FormField
            label="Function Name"
            error={errors.functionName?.message}
            helperText="The exported function to execute"
          >
            <Input
              {...register('functionName')}
              placeholder="generateReport"
              error={!!errors.functionName}
            />
          </FormField>

          {/* Cron Expression */}
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Cron Preset" helperText="Quick select common patterns">
              <Select onChange={handleCronPresetChange} defaultValue="">
                <option value="">Custom</option>
                {cronPresets.map((preset) => (
                  <option key={preset.value} value={preset.value}>
                    {preset.label}
                  </option>
                ))}
              </Select>
            </FormField>
            <FormField
              label="Cron Expression"
              error={errors.cronExpression?.message}
              helperText="minute hour day month weekday"
            >
              <Input
                {...register('cronExpression')}
                placeholder="0 0 * * *"
                error={!!errors.cronExpression}
              />
            </FormField>
          </div>

          {/* Timezone */}
          <FormField
            label="Timezone"
            error={errors.timezone?.message}
            helperText="IANA timezone for appJob execution"
          >
            <Select {...register('timezone')} error={!!errors.timezone}>
              {commonTimezones.map((tz) => (
                <option key={tz.value} value={tz.value}>
                  {tz.label}
                </option>
              ))}
            </Select>
          </FormField>

          {/* Payload */}
          <FormField
            label="Payload (JSON)"
            error={errors.payloadInput?.message}
            helperText="Optional JSON object to pass to the function"
          >
            <Textarea
              {...register('payloadInput')}
              placeholder='{"key": "value"}'
              rows={3}
              error={!!errors.payloadInput}
            />
          </FormField>

          {/* Description */}
          <FormField label="Description" error={errors.description?.message}>
            <Textarea
              {...register('description')}
              placeholder="Optional description for this appJob..."
              rows={2}
              error={!!errors.description}
            />
          </FormField>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={handleClose} disabled={isPending}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={isPending} disabled={!isDirty}>
              Save Changes
            </Button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};
